import { useState, useEffect, useMemo } from "react";
import { useRoute, useLocation, Link } from "wouter";
import { ArrowLeft, MapPin, Clock, CheckCircle, Plus, Store, Building2, Heart, Bookmark, Edit, Loader2, CreditCard, Coins, Phone, Navigation, Crown, Share2, Camera, AlertTriangle, Star, Car } from "lucide-react";
import { CategoryIcon } from "@/components/CategoryIcon";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

import { RouteCard } from "@/components/RouteCard";
import { PostCard } from "@/components/PostCard";
import { LoginModal } from "@/components/LoginModal";
import { ShopMapOsm } from "@/components/ShopMapOsm";
import { ShopRating } from "@/components/ShopRating";
import { ShopImage } from "@/components/ShopImage";
import { RatingModal } from "@/components/RatingModal";
import { EditShopModal } from "@/components/EditShopModal";
import { PriceLevel } from "@/components/PriceLevel";
import { PostComposerModal } from "@/components/PostComposerModal";
import { PostDetailModal } from "@/components/PostDetailModal";
import { ShareModal } from "@/components/ShareModal";
import { useAuth } from "@/lib/authContext";
import { useToast } from "@/hooks/use-toast";
import { useShopInteractions } from "@/contexts/ShopInteractionsContext";
import { getShopById, getPostsByShop, getRoutesByShop } from "@/lib/dummyData";
import { getOpeningStatus } from "@/lib/openingHours";
import { supabase } from "@/lib/supabase";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getShopNavigationUrl, openGoogleMaps } from "@/lib/googleMaps";
import { logAnalyticsEvent } from "@/lib/analytics";
import { ReportShopDialog } from "@/components/ReportShopDialog";
import { getAuthHeader } from "@/lib/authHelpers";
import { useRouteBuilder } from "@/contexts/RouteBuilderContext";
import { useI18n } from "@/lib/i18n";
import { TranslatableText } from "@/components/TranslatableText";
import { UpgradeModal } from "@/components/UpgradeModal";
import { useShopOwnerFeatures } from "@/lib/featureFlags";

export default function ShopDetailPage() {
  const [, navigate] = useLocation();
  const { isLoggedIn, user, tier, isInsider } = useAuth();
  const { t, locale } = useI18n();
  const { canSeeShopOwner } = useShopOwnerFeatures();
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [shop, setShop] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [ratingSummary, setRatingSummary] = useState<any>(null);
  const { toast } = useToast();
  const { getInteraction, toggleFavorite, toggleWishlist } = useShopInteractions();
  const [isToggling, setIsToggling] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editMode, setEditMode] = useState<"full" | "address-only" | "community-edit">("full");
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [composerIsUpdate, setComposerIsUpdate] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [ratingModalOpen, setRatingModalOpen] = useState(false);
  const [existingUserReview, setExistingUserReview] = useState<any>(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [selectedPost, setSelectedPost] = useState<any>(null);
  const [showDetail, setShowDetail] = useState(false);

  // Route builder context
  const { toggleShop, hasShop, shops: currentRouteShops, clearRoute, addShop } = useRouteBuilder();
  const isInRoute = shop?.id ? hasShop(shop.id) : false;
  const [showRoutePrompt, setShowRoutePrompt] = useState(false);

  const handleStartRoute = (action: 'add' | 'new') => {
    if (action === 'new') {
      clearRoute();
    }
    if (!hasShop(shop.id)) {
      addShop(shop);
    }
    setShowRoutePrompt(false);
    navigate("/route-builder");
  };

  const handleStartRouteClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!isInsider) {
      setShowUpgradeModal(true);
      return;
    }
    if (currentRouteShops.length > 0) {
      setShowRoutePrompt(true);
    } else {
      handleStartRoute('add');
    }
  };

  const isOwnerOrAdmin = shop && user && user.id && (
    user.id === shop.claimed_by_user_id || user.role === 'admin'
  );
  const [showClaimSuccess, setShowClaimSuccess] = useState(false);
  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);

  // Determine if this is a slug-based route or legacy ID route
  const slugRoute = useRoute("/kringloopwinkels/:province/:city/:name");
  const singularNlRoute = useRoute("/kringloopwinkel/:province/:city/:name");
  const thriftStoreRoute = useRoute("/thrift-store/:province/:city/:name");
  const secondHandShopRoute = useRoute("/second-hand-shop/:province/:city/:name");

  // Country-prefixed routes
  const countryNlRoute = useRoute("/:country/kringloopwinkel/:province/:city/:name");
  const countryThriftRoute = useRoute("/:country/thrift-store/:province/:city/:name");
  const countrySecondHandRoute = useRoute("/:country/second-hand-shop/:province/:city/:name");

  const legacyRoute = useRoute("/shops/:id");

  const slugParams = slugRoute[1] || singularNlRoute[1] || thriftStoreRoute[1] || secondHandShopRoute[1] ||
    countryNlRoute[1] || countryThriftRoute[1] || countrySecondHandRoute[1];
  const legacyParams = legacyRoute[1];


  useEffect(() => {
    async function fetchShop() {
      setLoading(true);
      try {
        if (slugParams?.province && slugParams?.city && slugParams?.name) {
          // Fetch by slug
          const response = await fetch(
            `/api/shops/slug/${slugParams.province}/${slugParams.city}/${slugParams.name}`
          );
          if (response.ok) {
            const shopData = await response.json();
            setShop(shopData);

            // Fetch rating summary to get VintMap review count
            try {
              const ratingResponse = await fetch(`/api/shops/${shopData.id}/ratings`);
              if (ratingResponse.ok) {
                const ratingData = await ratingResponse.json();
                setRatingSummary(ratingData);
              }
            } catch (ratingError) {
              console.error("Error fetching rating summary:", ratingError);
            }
          } else {
            setShop(null);
          }
        } else if (legacyParams?.id) {
          // Legacy ID-based lookup
          // In real app, we would fetch from API, but for now using dummy or fetch by ID
          const response = await fetch(`/api/shops/${legacyParams.id}`);
          if (response.ok) {
            const shopData = await response.json();
            setShop(shopData);
          } else {
            setShop(null);
          }
        } else {
          setShop(null);
        }
      } catch (error) {
        console.error("Error fetching shop:", error);
        setShop(null);
      } finally {
        setLoading(false);
      }
    }
    fetchShop();
  }, [slugParams?.province, slugParams?.city, slugParams?.name, legacyParams?.id, refreshKey]);

  const [posts, setPosts] = useState<any[]>([]);
  const [reviews, setReviews] = useState<any[]>([]);
  const [routes, setRoutes] = useState<any[]>([]);

  // Aggregate all photos (Shop > Reviews > Posts)
  const allShopPhotos = useMemo(() => {
    const photos = new Set<string>();

    // 1. Official Shop Photos
    if (shop?.imageUrl) photos.add(shop.imageUrl);
    if (shop?.fields?.photos?.value?.urls && Array.isArray(shop.fields.photos.value.urls)) {
      shop.fields.photos.value.urls.forEach((url: string) => photos.add(url));
    }

    // 2. Review Photos
    reviews.forEach(review => {
      if (review.photoUrls && Array.isArray(review.photoUrls)) {
        review.photoUrls.forEach((url: string) => photos.add(url));
      }
    });

    // 3. Post Photos (Only Shop Updates / Winkelnieuws)
    posts.filter(p => p.category === "Winkelnieuws").forEach(post => {
      if (post.imageUrl) photos.add(post.imageUrl);
      if (post.images && Array.isArray(post.images)) {
        post.images.forEach((url: string) => photos.add(url));
      }
    });

    return Array.from(photos);
  }, [shop, reviews, posts]);

  // Scroll to top when shop changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [shop?.id]);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("claim") === "success") {
      setShowClaimSuccess(true);
      // Clean up URL
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  useEffect(() => {
    if (!shop?.id) return;

    // Fetch Posts
    fetch(`/api/shops/${shop.id}/posts`)
      .then(res => {
        if (!res.ok) {
          console.error(`[ShopDetail] Posts fetch failed (${res.status})`);
          return [];
        }
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data)) setPosts(data);
      })
      .catch(err => console.error("Error fetching posts:", err));

    // Fetch Reviews
    fetch(`/api/shops/${shop.id}/reviews?limit=5`)
      .then(res => {
        if (!res.ok) {
          console.error(`[ShopDetail] Reviews fetch failed (${res.status})`);
          return { reviews: [] };
        }
        return res.json();
      })
      .then(data => {
        if (data?.reviews && Array.isArray(data.reviews)) setReviews(data.reviews);
      })
      .catch(err => console.error("Error fetching reviews:", err));

    // Use dummy routes for now
    setRoutes(getRoutesByShop(shop.id));

    // Log Profile View (Smart Caching/Analytics)
    logAnalyticsEvent(shop.id, 'view_profile');
  }, [shop?.id, refreshKey]);

  const loadUserReview = async () => {
    if (!isLoggedIn || !shop?.id) return;
    try {
      const headers = await getAuthHeader();
      const response = await fetch(`/api/shops/${shop.id}/reviews/my-review`, {
        headers: headers || undefined
      });
      if (response.ok) {
        const data = await response.json();
        setExistingUserReview(data.review || null);
      }
    } catch (error) {
      console.error("Error loading user review:", error);
    }
  };

  useEffect(() => {
    if (isLoggedIn && shop?.id) {
      loadUserReview();
    }
  }, [shop?.id, isLoggedIn, refreshKey]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="text-center py-16">
            <Store className="h-12 w-12 mx-auto text-muted-foreground mb-4 animate-spin" />
            <h3 className="font-semibold text-lg mb-2">{t("routeDetail.loading")}</h3>
          </div>
        </main>

      </div>
    );
  }

  if (!shop) {
    return (
      <div className="min-h-screen flex flex-col bg-background">
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="text-center py-16">
            <Store className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-semibold text-lg mb-2">{t("shopDetail.notFoundTitle")}</h3>
            <p className="text-muted-foreground mb-4">
              {t("shopDetail.notFoundDesc")}
            </p>
            <Link href="/kringloopwinkels">
              <Button>{t("shopDetail.backToShops")}</Button>
            </Link>
          </div>
        </main>

      </div>
    );
  }

  // Filter posts: when shop owner features are hidden, show all posts as user treasures
  const displayPosts = posts.length > 0 ? posts : [];
  const shopUpdates = canSeeShopOwner
    ? displayPosts.filter((p: any) => p.asShopId || (p.authorId === shop.claimed_by_user_id))
    : [];
  const userTreasures = canSeeShopOwner
    ? displayPosts.filter((p: any) => !p.asShopId && (p.authorId !== shop.claimed_by_user_id))
    : displayPosts;

  const shopRoutes = routes.length > 0 ? routes : [];
  const isVerifiedShop = shop.verification_status === "verified" || shop.is_verified || shop.isVerified;
  const hasBanner = Boolean(shop.bannerUrl) && isVerifiedShop;

  const handleAddToRoute = () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    if (!isInsider) {
      setShowUpgradeModal(true);
      return;
    }

    if (shop) {
      toggleShop(shop);
      toast({
        title: isInRoute ? t("shopCard.removedFromRoute") : t("shopCard.addedToRoute"),
        duration: 2000,
      });
    }
  };

  const shopInteractions = shop?.id ? getInteraction(shop.id) : { favorite: false, wishlist: false };
  const isFavorite = shopInteractions.favorite;
  const isInWishlist = shopInteractions.wishlist;

  const handleToggleAction = async (type: 'favorite' | 'wishlist') => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }

    if (isToggling || !shop?.id) return;
    setIsToggling(true);

    try {
      if (type === 'favorite') {
        const newState = await toggleFavorite(shop.id);
        toast({
          title: newState ? "Toegevoegd aan favorieten" : "Verwijderd uit favorieten",
          duration: 2000,
        });
      } else {
        const newState = await toggleWishlist(shop.id);
        toast({
          title: newState ? "Toegevoegd aan nog te bezoeken kringloopwinkels" : "Verwijderd uit nog te bezoeken kringloopwinkels",
          duration: 2000,
        });
      }
    } catch (error) {
      toast({
        title: "Er ging iets mis",
        description: "Probeer het later opnieuw.",
        variant: "destructive",
      });
    } finally {
      setIsToggling(false);
    }
  };

  const handleReactivateShop = async () => {
    try {
      const headers = await getAuthHeader();
      const res = await fetch(`/api/shops/${shop.id}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          ...(headers || {})
        },
        body: JSON.stringify({ status: "active" })
      });

      if (res.ok) {
        toast({ title: "Winkel heropend", description: "De winkel is weer zichtbaar voor bezoekers." });
        setRefreshKey(prev => prev + 1);
      } else {
        throw new Error("Mislukt");
      }
    } catch (err) {
      console.error(err);
      toast({ title: "Fout", description: "Kon winkel niet heropenen.", variant: "destructive" });
    }
  };

  const handleWriteReview = () => {
    if (!isLoggedIn) {
      setShowLoginModal(true);
      return;
    }
    setRatingModalOpen(true);
  };


  return (
    <div className="min-h-screen flex flex-col bg-background w-full max-w-full">
      <main className="flex-1 container mx-auto px-4 sm:px-6 lg:px-8 py-8 max-w-full">
        <Link href="/kringloopwinkels">
          <Button variant="ghost" className="mb-6" data-testid="button-back">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t("shopDetail.backToThriftShops")}
          </Button>
        </Link>
        {shop.is_temporarily_closed && (
          <Alert variant="destructive" className="mb-6 bg-red-50 border-red-200 text-red-800 animate-in fade-in slide-in-from-top-4 duration-500">
            <Clock className="h-5 w-5 text-red-600" />
            <AlertDescription className="text-base font-bold">
              {t("shopDetail.temporarilyClosed")}
              {shop.temporarily_closed_reason && `: ${shop.temporarily_closed_reason}`}
              {shop.temporarily_closed_until && (
                <span className="block text-sm font-normal mt-1 opacity-90">
                  {t("shopDetail.reopenExpectedOn")} {new Date(shop.temporarily_closed_until).toLocaleDateString(locale === "nl" ? "nl-NL" : locale === "en-gb" ? "en-GB" : `${locale}-${locale.toUpperCase()}`, { day: 'numeric', month: 'long', year: 'numeric' })}
                </span>
              )}
            </AlertDescription>
          </Alert>
        )}

        {isOwnerOrAdmin && shop.status === 'closed' && (
          <Alert variant="destructive" className="mb-6 bg-red-100 border-red-300 text-red-900">
            <Store className="h-5 w-5 text-red-700" />
            <div className="flex flex-col gap-2 w-full">
              <AlertDescription className="text-base font-bold">
                {t("shopDetail.permanentlyClosed")}
              </AlertDescription>
              <div className="text-sm opacity-90">
                {t("shopDetail.permanentlyClosedDesc")}
              </div>
              <Button
                size="sm"
                variant="default"
                className="w-fit mt-2 bg-red-600 hover:bg-red-700 text-white border-none"
                onClick={handleReactivateShop}
              >
                {t("shopDetail.reopenShop")}
              </Button>
            </div>
          </Alert>
        )}

        {/* Shop Banner: only use explicit banner URL, never auto-fallback to first photo */}
        {hasBanner && (
          <div className="relative w-full rounded-2xl overflow-hidden mb-6 shadow-lg group h-48 md:h-72">
            <img
              src={shop.bannerUrl}
              alt={typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || "Winkel"}
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          </div>
        )}

        {/* Shop Header text: always below banner */}
        <div className="mb-8">
          <div className="flex flex-wrap items-center gap-2 mb-2">
            <h1 className="text-3xl md:text-4xl font-extrabold text-foreground break-words max-w-full" data-testid="text-shop-name">
              {typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || "Naamloos"}
            </h1>
            {shop.isVerified && (
              <CheckCircle className="h-6 w-6 text-primary" />
            )}
            {shop.price_level_avg && (
              <PriceLevel
                level={shop.price_level_avg}
                country={shop.country || "NL"}
                size="md"
                className="ml-1"
              />
            )}
            {shop.parking_rating_avg && (
              <div className="flex text-primary gap-0.5 ml-1 items-center" title={`${shop.parking_rating_avg}/3 Parkeren`}>
                {Array.from({ length: Math.round(shop.parking_rating_avg) }).map((_, i) => (
                  <Car key={i} className="h-5 w-5 fill-primary" />
                ))}
              </div>
            )}
          </div>
          <div className="flex items-start gap-2 text-muted-foreground min-w-0">
            <MapPin className="h-4 w-4 shrink-0 mt-1" />
            <span className="flex-1 min-w-0 break-words">{typeof shop.address === 'string' ? shop.address : (shop.address as any)?.value || ""}, {typeof shop.city === 'string' ? shop.city : (shop.city as any)?.value || ""}</span>
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 shrink-0"
              onClick={() => {
                logAnalyticsEvent(shop.id, 'nav_profile');
                const url = getShopNavigationUrl(shop);
                openGoogleMaps(url);
              }}
              title="Navigeer naar deze winkel"
            >
              <Navigation className="h-3.5 w-3.5" />
            </Button>
          </div>
          <div className="flex flex-wrap items-center gap-4 mt-3 text-sm text-muted-foreground">
            <button
              onClick={() => handleToggleAction('favorite')}
              className={`flex items-center gap-1.5 transition-colors hover:text-primary ${isFavorite ? 'text-primary' : ''}`}
              title={isFavorite ? t("shopCard.removeFavorite") : t("shopCard.addFavorite")}
            >
              <Heart className={`h-4 w-4 ${isFavorite ? 'fill-current' : ''}`} />
              <span className="font-medium text-foreground">{shop.likesCount || 0}</span>
            </button>
            <button
              onClick={() => handleToggleAction('wishlist')}
              className={`flex items-center gap-1.5 transition-colors hover:text-primary ${isInWishlist ? 'text-primary' : ''}`}
              title={isInWishlist ? t("shopCard.removeWishlist") : t("shopCard.addWishlist")}
            >
              <Bookmark className={`h-4 w-4 ${isInWishlist ? 'fill-current' : ''}`} />
              <span className="font-medium text-foreground">{shop.savesCount || 0}</span>
            </button>
          </div>
        </div>

        {/* Verification & Dashboard Banner for Owner */}
        {isOwnerOrAdmin && (
          <div className="mb-8 p-4 bg-primary/5 border border-primary/10 rounded-xl space-y-4">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Building2 className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-bold">{t("shopDetail.adminPanel")}</h4>
                  <p className="text-sm text-muted-foreground">
                    {shop.claimed_by_user_id === user?.id
                      ? t("shopDetail.adminPanelDesc")
                      : "Je hebt beheerdersrechten (Admin) voor deze winkel."}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 w-full sm:w-auto">
                <Link href="/account">
                  <Button variant="secondary" size="sm">
                    {t("shopDetail.thriftProfileDashboard")}
                  </Button>
                </Link>
                {shop.verification_status === 'verified' && (
                  <Button variant="outline" size="sm" onClick={() => { setEditMode("full"); setEditModalOpen(true); }}>
                    {t("shopDetail.editShop")}
                  </Button>
                )}
              </div>
            </div>

            {shop.verification_status === 'pending_letter' && (
              <div className="pt-4 border-t border-primary/10 space-y-3">
                <Alert className="bg-yellow-50 border-yellow-200">
                  <Clock className="h-4 w-4 text-yellow-600" />
                  <AlertDescription className="text-yellow-700">
                    <strong>{t("shopDetail.verificationPending")}:</strong> {t("shopDetail.verificationPendingDesc")} <strong>{shop.address}</strong>.
                    {t("shopDetail.enterCodeUnlock")}
                  </AlertDescription>
                </Alert>
                <div className="flex flex-col sm:flex-row items-end gap-3 max-w-md">
                  <div className="w-full space-y-1.5">
                    <Label htmlFor="verification-code" className="text-xs uppercase font-bold tracking-wider text-muted-foreground">{t("shopDetail.verificationCode")}</Label>
                    <Input
                      id="verification-code"
                      placeholder={t("shopDetail.verificationCodePlaceholder")}
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
                      className="uppercase font-mono"
                    />
                    {shop.verification_code && (
                      <p className="text-[10px] text-muted-foreground">
                        <span className="font-semibold text-primary">{t("shopDetail.testTip")}:</span> {t("shopDetail.yourCodeIs")} {shop.verification_code}
                      </p>
                    )}
                  </div>
                  <Button
                    onClick={async () => {
                      if (!verificationCode) return;
                      setIsVerifying(true);
                      try {
                        const token = (await supabase.auth.getSession()).data.session?.access_token;
                        const res = await fetch(`/api/shops/${shop.id}/verify-code`, {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json',
                            'Authorization': `Bearer ${token}`
                          },
                          body: JSON.stringify({ code: verificationCode })
                        });
                        if (!res.ok) {
                          const err = await res.json();
                          throw new Error(err.error || t("shopDetail.verifyError"));
                        }
                        toast({ title: t("shopDetail.congrats"), description: t("shopDetail.shopFullyVerified") });
                        setRefreshKey(prev => prev + 1);
                      } catch (e: any) {
                        toast({ title: t("common.error"), description: e.message, variant: "destructive" });
                      } finally {
                        setIsVerifying(false);
                      }
                    }}
                    disabled={isVerifying || !verificationCode}
                  >
                    {isVerifying ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
                    {t("shopDetail.verifyCode")}
                  </Button>
                </div>
              </div>
            )}

            {shop.verification_status === 'verified' && (
              <div className="pt-2 flex items-center gap-2 text-green-600">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm font-medium">{t("shopDetail.shopPremiumVisible")}</span>
              </div>
            )}
          </div>
        )}

        {/* Shop Metadata & Actions */}
        <div className="flex flex-col gap-6 mb-8">
          <div className="flex flex-col md:flex-row justify-between items-start gap-4">
            <div className="space-y-4 w-full md:w-auto">
              <div className="flex flex-wrap gap-2">
                {shop.has_charity && (
                  <Badge variant="secondary" className="bg-green-100 text-green-800 border-green-200">
                    {t("addShop.charity")}
                  </Badge>
                )}
                {shop.has_horeca && (
                  <Badge variant="secondary" className="bg-primary/10 text-primary border-primary/20">
                    {t("addShop.horeca")}
                  </Badge>
                )}
              </div>

              {/* Actions moved to the bottom */}
            </div>

            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 w-full md:w-auto">
              {/* Category Icons row */}
              {shop.categories && Array.isArray(shop.categories) && shop.categories.length > 0 && (
                <div className="flex items-center gap-1.5 mb-2 sm:mb-0 sm:mr-2 flex-wrap">
                  {shop.categories.map((cat: string) => (
                    <div
                      key={cat}
                      className="flex items-center justify-center w-8 h-8 rounded-full bg-black text-white transition-colors hover:bg-black/80 cursor-help shadow-sm"
                      title={cat}
                    >
                      <CategoryIcon category={cat} size={14} className="text-white" />
                    </div>
                  ))}

                  {/* Info Button with Popover */}
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 rounded-full bg-muted/50 hover:bg-muted text-muted-foreground"
                        title={t("addShop.categories")}
                      >
                        <Info className="h-4 w-4" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-64 p-3" align="end">
                      <h4 className="font-bold text-sm mb-2">{t("addShop.categories")}</h4>
                      <div className="space-y-2">
                        {shop.categories.map((cat: string) => (
                          <div key={cat} className="flex items-center gap-2 text-sm text-foreground">
                            <div className="flex items-center justify-center w-6 h-6 rounded-full bg-black text-white">
                              <CategoryIcon category={cat} size={12} className="text-white" />
                            </div>
                            <span>{cat}</span>
                          </div>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              )}


              <div className="flex w-full sm:w-auto sm:items-center">
                <Button
                  onClick={handleAddToRoute}
                  className={`w-full font-medium ${isInRoute
                    ? 'bg-primary/10 hover:bg-primary/15 border-primary text-primary'
                    : 'bg-primary text-primary-foreground'
                    }`}
                  variant={isInRoute ? "outline" : "default"}
                >
                  {isInRoute ? (
                    <CheckCircle className="h-4 w-4 mr-2" />
                  ) : (
                    <Plus className="h-4 w-4 mr-2" />
                  )}
                  {isInRoute ? t("shopCard.inRoute") : t("shopDetail.route")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Aggregated Photo Gallery - from Shop, Reviews & Posts */}
        {allShopPhotos.length > 0 && (
          <div className="mb-8">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
              <h2 className="text-xl font-bold flex items-center gap-2">
                {t("shopDetail.shopPhotos")}
                <Badge variant="secondary" className="text-xs">{allShopPhotos.length}</Badge>
              </h2>
              {allShopPhotos.length > 6 && (
                <Button variant="ghost" size="sm" onClick={() => setGalleryOpen(true)}>
                  {t("common.viewAll", { count: allShopPhotos.length })}
                </Button>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 sm:gap-4">
              {allShopPhotos.slice(0, 6).map((url: string, idx: number) => (
                <div
                  key={idx}
                  className={`relative overflow-hidden rounded-lg border border-border group aspect-square cursor-pointer`}
                  onClick={() => setGalleryOpen(true)}
                >
                  <img
                    src={url}
                    alt={`${typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || "Winkel"} foto ${idx + 1}`}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  {idx === 5 && allShopPhotos.length > 6 && (
                    <div className="absolute inset-0 bg-black/60 flex flex-col items-center justify-center text-white backdrop-blur-[2px]">
                      <Plus className="h-6 w-6 mb-1" />
                      <span className="font-bold text-sm">+{allShopPhotos.length - 6}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Full Gallery Dialog */}
        <Dialog open={galleryOpen} onOpenChange={setGalleryOpen}>
          <DialogContent className="w-[calc(100vw-1rem)] sm:w-full max-w-4xl max-h-[90vh] flex flex-col p-4 sm:p-6">
            <DialogHeader className="mb-4">
              <DialogTitle>Alle foto's van {typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || "Winkel"}</DialogTitle>
            </DialogHeader>
            <ScrollArea className="flex-1 -mr-2 pr-2">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 pb-4">
                {allShopPhotos.map((url: string, idx: number) => (
                  <div key={idx} className="relative overflow-hidden rounded-xl aspect-square border group">
                    <img
                      src={url}
                      alt={`Foto ${idx + 1}`}
                      className="w-full h-full object-cover transition-all hover:scale-105"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>
            </ScrollArea>
          </DialogContent>
        </Dialog>

        <div className="grid md:grid-cols-3 gap-8 items-start">
          {/* LEFT COLUMN: Info, Map, Hours */}
          <div className="md:col-span-1 space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Store className="h-5 w-5" /> {t("shopDetail.shopInfo")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <MapPin className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <p className="font-medium">{t("shopDetail.address")}</p>
                    <p className="text-sm text-muted-foreground">{shop.address}</p>
                    <p className="text-sm text-muted-foreground">{shop.postal_code} {shop.city}</p>
                    <p className="text-sm text-muted-foreground">{shop.province}</p>
                  </div>
                </div>

                {shop.description && (
                  <div className="space-y-1">
                    <p className="font-medium">{t("shopDetail.aboutShop")}</p>
                    <TranslatableText
                      table="shops"
                      rowId={shop.id}
                      text={shop.description}
                      translations={shop.translations}
                      className="text-sm text-muted-foreground whitespace-pre-line"
                    />
                  </div>
                )}

                {shop.phone_number && (
                  <div className="flex items-start gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground mt-0.5" />
                    <div>
                      <p className="font-medium">{t("shopDetail.phone")}</p>
                      <p className="text-sm text-muted-foreground">
                        <a href={`tel:${shop.phone_number}`} className="hover:text-primary transition-colors">
                          {shop.phone_number}
                        </a>
                      </p>
                    </div>
                  </div>
                )}

                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-muted-foreground mt-0.5" />
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium">{t("shopDetail.openingHours")}</p>
                      {(() => {
                        const todayIndex = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1;
                        const status = getOpeningStatus(
                          shop.openingHoursStructured || [],
                          todayIndex,
                          {
                            isClosed: !!shop.is_temporarily_closed,
                            until: shop.temporarily_closed_until,
                            reason: shop.temporarily_closed_reason
                          },
                          { locale, t }
                        );
                        return (
                          <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${status.isOpen ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                            {status.status.split(':')[0]}
                          </span>
                        );
                      })()}
                    </div>
                    {shop.openingHoursStructured && shop.openingHoursStructured.length > 0 ? (
                      <div className="text-sm space-y-1 mt-2">
                        {[t("shops.filters.day.monday"), t("shops.filters.day.tuesday"), t("shops.filters.day.wednesday"), t("shops.filters.day.thursday"), t("shops.filters.day.friday"), t("shops.filters.day.saturday"), t("shops.filters.day.sunday")].map((day, idx) => {
                          const oh = shop.openingHoursStructured.find((h: any) => h.weekday === idx);
                          const isToday = new Date().getDay() === (idx + 1) % 7;

                          return (
                            <div key={day} className={`flex justify-between gap-4 ${isToday ? "font-bold text-foreground" : "text-muted-foreground"}`}>
                              <span className="w-24">{day}</span>
                              <span className="tabular-nums">
                                {oh?.is_closed || !oh
                                  ? t("common.closed")
                                  : `${oh.open_time?.slice(0, 5)} - ${oh.close_time?.slice(0, 5)}`
                                }
                              </span>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-muted-foreground whitespace-pre-line mt-1">
                        {typeof shop.openingHours === 'string' ? shop.openingHours : t("shopDetail.noOpeningHoursKnown")}
                      </p>
                    )}
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <p className="font-medium mb-3">{t("shopDetail.paymentOptions")}</p>
                  {(shop.has_card_payment || shop.has_cash_payment) ? (
                    <div className="flex gap-4">
                      <div className={`flex items-center gap-2 text-sm ${shop.has_card_payment ? 'text-foreground font-medium' : 'text-muted-foreground line-through opacity-50'}`}>
                        <CreditCard className="h-4 w-4" />
                        {t("addShop.cardPayment")}
                      </div>
                      <div className={`flex items-center gap-2 text-sm ${shop.has_cash_payment ? 'text-foreground font-medium' : 'text-muted-foreground line-through opacity-50'}`}>
                        <Coins className="h-4 w-4" />
                        {t("addShop.cashPayment")}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground italic">{t("routeDetail.unknown")}</p>
                  )}
                </div>
              </CardContent>
            </Card>


            <Card className="overflow-hidden w-full flex justify-center">
              <div className="w-full max-w-full overflow-hidden flex justify-center">
                <ShopMapOsm shop={shop} height="300px" className="h-[300px] w-full" />
              </div>
            </Card>
          </div>

          {/* RIGHT COLUMN: Feed, Posts, Reviews */}
          <div className="md:col-span-2 space-y-8">

            {/* Owner Composer Widget - Prominently at top of feed */}
            {isOwnerOrAdmin && (
              <Card className="border-2 border-primary/20 shadow-sm overflow-hidden animate-in fade-in slide-in-from-bottom-4 duration-500 w-full">
                <CardContent className="p-4 sm:p-6 bg-gradient-to-br from-white to-primary/5 flex flex-col items-center sm:items-stretch">
                  <div className="flex gap-4 mb-4 w-full">
                    <Avatar className="h-10 w-10 sm:h-12 sm:w-12 border-2 border-white shadow-sm cursor-pointer shrink-0">
                      <AvatarImage src={shop.imageUrl} />
                      <AvatarFallback className="bg-primary/10 text-primary">
                        <Store className="h-6 w-6" />
                      </AvatarFallback>
                    </Avatar>
                    <div
                      className="bg-white hover:bg-white/80 border border-border transition-all rounded-full flex-1 px-4 py-2.5 sm:py-3 cursor-pointer text-muted-foreground flex items-center shadow-inner text-sm sm:text-base min-w-0"
                      onClick={() => {
                        setComposerIsUpdate(true);
                        setComposerOpen(true);
                      }}
                    >
                      <span className="truncate">
                        {t("shopDetail.shareWithFollowers", { name: user?.username || "" })}
                      </span>
                    </div>
                  </div>
                  <div className="flex justify-between border-t border-border/60 pt-3 mt-2">
                    <Button
                      variant="ghost"
                      className="flex-1 gap-2 hover:bg-green-50 hover:text-green-700 transition-colors"
                      onClick={() => {
                        setComposerIsUpdate(true);
                        setComposerOpen(true);
                      }}
                    >
                      <Camera className="h-5 w-5 text-green-600 sm:hidden" />
                      <span className="hidden sm:inline">{t("shopDetail.uploadGem")}</span>
                      <span className="sm:hidden">{t("shopDetail.camera")}</span>
                    </Button>
                    <Button
                      variant="ghost"
                      className="flex-1 gap-2 hover:bg-blue-50 hover:text-blue-700 transition-colors"
                      onClick={() => {
                        setComposerIsUpdate(true);
                        setComposerOpen(true);
                      }}
                    >
                      <Edit className="h-5 w-5 text-blue-600" />
                      <span className="hidden sm:inline">{t("shopDetail.postUpdate")}</span>
                      <span className="sm:hidden">{t("shopDetail.update")}</span>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Shop Posts / Updates */}
            {shopUpdates.length > 0 && (
              <section>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold flex items-center gap-2">
                    <Crown className="h-5 w-5 text-yellow-500" />
                    {t("shopDetail.shopUpdates")}
                  </h2>
                </div>
                <div className="grid grid-cols-2 gap-2 sm:gap-4">
                  {shopUpdates.slice(0, 4).map((post: any) => (
                    <div
                      key={post.id}
                      className="aspect-square relative overflow-hidden rounded-lg cursor-pointer group"
                      onClick={() => {
                        setSelectedPost(post);
                        setShowDetail(true);
                      }}
                    >
                      <img
                        src={(post.images && post.images[0]) || post.imageUrl}
                        alt=""
                        className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-500"
                      />
                      <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  ))}
                </div>
                {shopUpdates.length > 4 && (
                  <div className="mt-4 flex justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/shops/${shop.id}/posts?type=update`)}
                      className="w-full sm:w-auto"
                    >
                      {t("common.viewAll", { count: shopUpdates.length })}
                    </Button>
                  </div>
                )}
              </section>
            )}

            {/* Treasures (User Posts) */}
            <section>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
                <h2 className="text-xl font-bold">{t("shopDetail.foundGems")}</h2>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto sm:justify-end">
                  {userTreasures.length > 4 && (
                    <Button variant="ghost" size="sm">{t("common.viewAll", { count: userTreasures.length })}</Button>
                  )}
                  <Button variant="outline" size="sm" onClick={() => setComposerOpen(true)}>
                    <Camera className="h-4 w-4 mr-2 sm:hidden" />
                    <span className="hidden sm:inline">{t("shopDetail.uploadGem")}</span>
                    <span className="sm:hidden">{t("shopDetail.camera")}</span>
                  </Button>
                </div>
              </div>

              {userTreasures.length > 0 ? (
                <>
                  <div className="grid grid-cols-2 gap-2 sm:gap-4">
                    {userTreasures.slice(0, 4).map((post: any) => (
                      <div
                        key={post.id}
                        className="aspect-square relative overflow-hidden rounded-lg cursor-pointer group"
                        onClick={() => {
                          setSelectedPost(post);
                          setShowDetail(true);
                        }}
                      >
                        <img
                          src={(post.images && post.images[0]) || post.imageUrl}
                          alt=""
                          className="w-full h-full object-cover transition-transform group-hover:scale-105 duration-500"
                        />
                        <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity" />
                      </div>
                    ))}
                  </div>
                  {userTreasures.length > 4 && (
                    <div className="mt-4 flex justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate(`/shops/${shop.id}/posts?type=gem`)}
                        className="w-full sm:w-auto"
                      >
                        {t("common.viewAll", { count: userTreasures.length })}
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-8 bg-muted/30 rounded-lg border border-dashed border-muted-foreground/25">
                  <p className="text-muted-foreground italic mb-4">{t("shopDetail.noGemsShared")}</p>
                  <Button onClick={() => setComposerOpen(true)}>
                    <Camera className="h-4 w-4 mr-2 sm:hidden" />
                    <span className="hidden sm:inline">{t("shopDetail.uploadGem")}</span>
                    <span className="sm:hidden">{t("shopDetail.camera")}</span>
                  </Button>
                </div>
              )}
            </section>

            {/* Routes */}
            <section>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
                <h2 className="text-xl font-bold">{t("shopDetail.inRoutes")}</h2>
                <div className="flex flex-wrap gap-2 w-full sm:w-auto sm:justify-end">
                  {shopRoutes.length > 3 && (
                    <Button variant="ghost" size="sm">{t("common.viewAll", { count: shopRoutes.length })}</Button>
                  )}
                </div>
              </div>

              {shopRoutes.length > 0 ? (
                <div className="grid lg:grid-cols-2 gap-4">
                  {shopRoutes.slice(0, 4).map((route: any) => (
                    <RouteCard key={route.id} route={route} showSaveButton={false} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-muted/30 rounded-lg">
                  <p className="text-muted-foreground italic mb-2">{t("shopDetail.notInRouteYet")}</p>
                  <Button variant="outline" size="sm" onClick={handleStartRouteClick}>
                    <MapPin className="h-4 w-4 mr-2" />
                    {t("account.startRoute")}
                  </Button>
                </div>
              )}
            </section>

            {/* Reviews Section */}
            <section id="reviews">
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-4">
                <h2 className="text-xl font-bold">{t("shopDetail.reviews")}</h2>
                <Link href={`/shops/${shop.id}/reviews`}>
                  <Button variant="ghost" className="w-full sm:w-auto">{t("shopDetail.viewAllReviews")}</Button>
                </Link>
              </div>

              <div className="bg-muted/30 rounded-lg p-6 border border-border">
                <div className="grid md:grid-cols-2 gap-8 items-center mb-6">
                  <div>
                    <div className="text-4xl font-bold mb-2">
                      {shop.rating ? shop.rating.toFixed(1) : "0.0"}
                    </div>
                    <div className="flex items-center gap-1 mb-1">
                      <span className="text-yellow-500">★★★★★</span>
                    </div>
                    <p className="text-muted-foreground">{t("shopDetail.reviewCount", { count: shop.vintMapReviewCount || 0 })}</p>
                  </div>

                  <ShopRating
                    communityRating={ratingSummary?.vintmap?.display_avg || shop.rating || null}
                    communityReviewCount={ratingSummary?.vintmap?.review_count || shop.vintMapReviewCount || 0}
                    ownerRating={shop.ownerRating}
                    googleRating={shop.google_rating}
                    googleReviewCount={shop.google_review_count}
                    googleReviewsUrl={shop.google_reviews_url}
                    showLabels={true}
                    shopId={shop.id}
                    shopName={typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || "Winkel"}
                    className="w-full max-w-xs"
                    onRatingUpdate={() => setRefreshKey(prev => prev + 1)}
                  />

                  <div className="mt-6">
                    <Button
                      className="w-full bg-[#FF4F9A] hover:bg-[#E63E85] text-white font-bold h-12 rounded-xl shadow-lg border-none"
                      onClick={handleWriteReview}
                    >
                      <Star className="h-5 w-5 mr-2" />
                      {t("shopDetail.writeReview")}
                    </Button>
                  </div>
                </div>

                <div className="space-y-4">
                  {reviews.length > 0 ? (
                    reviews.map((review: any) => (
                      <div key={review.id} className="border-b border-border pb-4 last:border-0 last:pb-0">
                        <div className="flex items-center gap-3 mb-2">
                          <Avatar className="h-8 w-8">
                            {review.user?.avatarUrl ? (
                              <img src={review.user.avatarUrl} alt={review.user.username} className="h-full w-full object-cover rounded-full" />
                            ) : (
                              <AvatarFallback className="text-xs bg-primary/10 text-primary">
                                {review.user?.username?.[0].toUpperCase() || 'U'}
                              </AvatarFallback>
                            )}
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center justify-between">
                              <div className="font-semibold">{review.user?.username || t("shopReviews.userFallback")}</div>
                              <span className="text-xs text-muted-foreground">
                                {new Date(review.created_at || review.createdAt).toLocaleDateString(locale === "nl" ? "nl-NL" : locale === "en-gb" ? "en-GB" : `${locale}-${locale.toUpperCase()}`)}
                              </span>
                            </div>
                          </div>
                        </div>
                        {(review.rating_atmosphere || review.rating_service || review.rating_price) && (
                          <div className="flex gap-2 text-xs text-muted-foreground mb-2">
                            {review.rating_atmosphere && <span>{t("shopReviews.atmosphere")}: {review.rating_atmosphere}</span>}
                            {review.rating_service && <span>{t("shopReviews.service")}: {review.rating_service}</span>}
                            {review.rating_price && <span>{t("rating.assortment")}: {review.rating_price}</span>}
                            {review.price_level && (
                              <span className="flex items-center gap-1">
                                {t("rating.price")}: <PriceLevel level={review.price_level} size="sm" />
                                <span className="ml-1 text-xs">({review.price_level}/5)</span>
                              </span>
                            )}
                          </div>
                        )}
                        {/* Display Photos explicitly within the review if available */}
                        {review.photoUrls && review.photoUrls.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {review.photoUrls.map((url: string, idx: number) => (
                              <img key={idx} src={url} alt={`Review photo ${idx + 1}`} className="w-16 h-16 object-cover rounded-md border" />
                            ))}
                          </div>
                        )}
                        <TranslatableText
                          table="reviews"
                          rowId={review.id}
                          text={review.comment}
                          translations={review.translations}
                          className="text-sm"
                        />
                      </div>
                    ))
                  ) : (
                    <p className="text-muted-foreground italic">{t("shopDetail.noReviewsPosted")}</p>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>

        {/* BOTTOM ACTIONS SECTION (Share, Edit, Claim) */}
        <div className="mt-12 pt-8 border-t border-border flex flex-col items-center gap-6 pb-8">
          <div className="flex flex-wrap justify-center gap-3">
            <Button variant="outline" onClick={() => setShareModalOpen(true)}>
              <Share2 className="h-4 w-4 mr-2" />
              {t("routeDetail.share")}
            </Button>

            {shop && !isOwnerOrAdmin && (
              <>
                <Button
                  variant="outline"
                  onClick={() => { setEditMode("address-only"); setEditModalOpen(true); }}
                >
                  <MapPin className="h-4 w-4 mr-2" />
                  {t("shopDetail.changeAddress")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => { setEditMode("community-edit"); setEditModalOpen(true); }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  {t("shopDetail.improveInfo")}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setReportDialogOpen(true)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                >
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  {t("shopDetail.reportProblem")}
                </Button>
              </>
            )}

            {isOwnerOrAdmin && (
              <Button variant="outline" onClick={() => { setEditMode("full"); setEditModalOpen(true); }}>
                <Edit className="h-4 w-4 mr-2" />
                {t("common.edit")}
              </Button>
            )}
          </div>

          {/* Claim button: only shown when shop owner features are enabled */}
          {canSeeShopOwner && !shop.isVerified && (
            <div className="mt-4">
              <Link href={`/claim-shop/${shop.id}`}>
                <Button variant="ghost" className="text-muted-foreground hover:text-primary">
                  <Building2 className="h-4 w-4 mr-2" />
                  {t("shopDetail.areYouOwner")}
                </Button>
              </Link>
            </div>
          )}
        </div>
      </main >



      <LoginModal
        open={showLoginModal}
        onOpenChange={setShowLoginModal}
        action="route"
      />

      <UpgradeModal
        open={showUpgradeModal}
        onOpenChange={setShowUpgradeModal}
        variant="route-builder"
      />

      {/* Route Prompt Dialog */}
      <Dialog open={showRoutePrompt} onOpenChange={setShowRoutePrompt}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("routeBuilder.composeRoute")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <p className="text-muted-foreground">
              {t("shopDetail.addToExistingOrNewRoute")}
            </p>
            <div className="flex flex-col gap-3 pt-2">
              <Button onClick={() => handleStartRoute('add')} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                {t("shopDetail.addToExistingRoute")}
              </Button>
              <Button variant="outline" onClick={() => handleStartRoute('new')} className="w-full">
                <MapPin className="h-4 w-4 mr-2" />
                {t("shopDetail.startNewRoute")}
              </Button>
              <Button variant="ghost" onClick={() => setShowRoutePrompt(false)} className="w-full">
                {t("shops.cancel")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Claim Success Dialog: only relevant when shop owner features are enabled */}
      {canSeeShopOwner && (
        <Dialog open={showClaimSuccess} onOpenChange={setShowClaimSuccess}>
          <DialogContent className="sm:max-w-md text-center">
            <DialogHeader>
              <div className="mx-auto w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4">
                <CheckCircle className="h-10 w-10 text-green-600" />
              </div>
              <DialogTitle className="text-2xl font-bold">{t("shopDetail.shopClaimed")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-muted-foreground">
                {t("shopDetail.claimCongrats")} <strong>{shop?.name}</strong> {t("shopDetail.claimSuccessSuffix")}
              </p>
              <div className="bg-muted p-4 rounded-lg text-sm text-left space-y-2">
                <p className="font-semibold">{t("shopDetail.whatNow")}</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>{t("shopDetail.whatNow1")}</li>
                  <li>{t("shopDetail.whatNow2")}</li>
                  <li>{t("shopDetail.whatNow3")}</li>
                </ul>
              </div>
            </div>
            <div className="flex justify-center">
              <Button onClick={() => setShowClaimSuccess(false)} className="w-full">
                {t("shopDetail.getStarted")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      <ShareModal
        open={shareModalOpen}
        onOpenChange={setShareModalOpen}
        title={(function () {
          const nameVal = shop?.name;
          return typeof nameVal === 'string' ? nameVal : (nameVal as any)?.value || "Kringloopwinkel";
        })()}
        text={(function () {
          const name = typeof shop?.name === 'string' ? shop.name : (shop?.name as any)?.value || "Kringloopwinkel";
          const address = typeof shop?.address === 'string' ? shop.address : (shop?.address as any)?.value || "";
          const city = typeof shop?.city === 'string' ? shop.city : (shop?.city as any)?.value || "";
          const rating = shop?.rating?.toFixed(1) || "-";
          const reviews = shop?.vintMapReviewCount || 0;

          return `${t("shopDetail.sharePrefix")} ${name} ${t("shopDetail.shareSuffix")}\n\n📍 ${address}, ${city}\n⭐ ${rating} (${reviews} ${t("shopDetail.reviewsLabel")})`;
        })()}
        url={window.location.href}
        imageUrl={shop?.share_image_url || undefined}
        entityType="shop"
        entityId={shop?.id}
      />

      <PostComposerModal
        open={composerOpen}
        onOpenChange={(v) => {
          setComposerOpen(v);
          if (!v) setComposerIsUpdate(false); // Reset mode on close
        }}
        defaultPostAs={shop?.id}
        shopId={shop?.id}
        isShopUpdate={composerIsUpdate}
      />
      {shop && (
        <EditShopModal
          shop={shop}
          open={editModalOpen}
          onOpenChange={setEditModalOpen}
          onShopUpdated={() => setRefreshKey(prev => prev + 1)}
          mode={editMode}
        />
      )}

      <RatingModal
        open={ratingModalOpen}
        onOpenChange={setRatingModalOpen}
        shopId={shop.id}
        shopName={typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || "Winkel"}
        existingReview={existingUserReview}
        onSuccess={() => setRefreshKey(prev => prev + 1)}
      />

      <ReportShopDialog
        open={reportDialogOpen}
        onOpenChange={setReportDialogOpen}
        shopId={shop.id}
        shopName={typeof shop.name === 'string' ? shop.name : (shop.name as any)?.value || "Winkel"}
      />

      <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-background/95 backdrop-blur-sm border-t p-2 flex items-center justify-around z-50 shadow-[0_-4px_12px_rgba(0,0,0,0.05)]">
        <button
          onClick={() => handleToggleAction('favorite')}
          className={`flex flex-col items-center gap-1 text-[10px] font-medium ${isFavorite ? 'text-primary' : 'text-muted-foreground'}`}
        >
          <Heart className={`h-5 w-5 ${isFavorite ? 'fill-current' : ''}`} />
          {t("shopDetail.like")}
        </button>
        <button
          onClick={() => handleToggleAction('wishlist')}
          className={`flex flex-col items-center gap-1 text-[10px] font-medium ${isInWishlist ? 'text-primary' : 'text-muted-foreground'}`}
        >
          <Bookmark className={`h-5 w-5 ${isInWishlist ? 'fill-current' : ''}`} />
          {t("shopDetail.bucketlist")}
        </button>
        <button
          onClick={handleAddToRoute}
          className="flex flex-col items-center gap-1 text-[10px] font-medium text-muted-foreground"
        >
          <Plus className="h-5 w-5" />
          {t("shopDetail.route")}
        </button>
        <button
          onClick={() => setShareModalOpen(true)}
          className="flex flex-col items-center gap-1 text-[10px] font-medium text-muted-foreground"
        >
          <Share2 className="h-5 w-5" />
          {t("common.share")}
        </button>
      </div>

      <PostDetailModal
        post={selectedPost}
        open={showDetail}
        onOpenChange={setShowDetail}
        onLikeChange={() => setRefreshKey(prev => prev + 1)}
      />

      <div className="h-20 sm:hidden"></div> {/* Spacer for bottom bar */}
    </div >
  );
}
